import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import { useNavigate, useParams } from 'react-router-dom';
import { usePassCodeur } from '../../lib/pass-codeur-context';
import { getCourseByIdStatic } from '../../pages/cours/courses-data';
import { CourseNavigation } from './CourseNavigation';
import { Separator } from './CourseComponents';
import Breadcrumbs, { generateCourseBreadcrumbs } from '../ui/breadcrumbs';

// Fonction utilitaire pour trouver le chapitre suivant
const getNextChapter = (courseStructure, currentChapterId) => {
  const currentIndex = courseStructure.chapters.findIndex(
    chapter => chapter.id === currentChapterId
  );
  
  if (currentIndex >= 0 && currentIndex < courseStructure.chapters.length - 1) {
    return courseStructure.chapters[currentIndex + 1];
  }
  
  return null;
};

// Fonction utilitaire pour trouver le chapitre précédent
const getPreviousChapter = (courseStructure, currentChapterId) => {
  const currentIndex = courseStructure.chapters.findIndex(
    chapter => chapter.id === currentChapterId
  );
  
  if (currentIndex > 0) {
    return courseStructure.chapters[currentIndex - 1];
  }
  
  return null;
};

const GenericCourseContainer = ({
  // Props obligatoires
  courseStructure,
  chapterContents,
  
  // Composant de Layout à utiliser
  LayoutComponent,
  
  // Props spécifiques au layout
  layoutProps = {},
  
  // Fonction pour formater le titre du chapitre (par défaut: affiche simplement le titre)
  formatTitle = (chapitreId, chapterTitle) => chapterTitle,
  
  // Autres props personnalisables
  contentProps = {}
}) => {
  const { currentUser } = useAuth();
  const { hasAccessToCourses } = usePassCodeur();
  const navigate = useNavigate();
  const { chapitreId } = useParams();
  const decodedChapitreId = chapitreId ? decodeURIComponent(chapitreId) : chapitreId;
  const [courseInfo, setCourseInfo] = useState(null);
  
  // Validation de courseStructure
  if (!courseStructure) {
    console.error('GenericCourseContainer: courseStructure est undefined');
    return <div>Erreur: Structure de cours manquante</div>;
  }
  
  if (!courseStructure.chapters) {
    console.error('GenericCourseContainer: courseStructure.chapters est undefined', courseStructure);
    return <div>Erreur: Chapitres du cours manquants</div>;
  }
  
  // Récupérer l'ID du cours depuis la structure
  const courseId = courseStructure.id;
  
  // Obtenir les infos du cours depuis les données statiques
  useEffect(() => {
    const loadCourseInfo = () => {
      try {
        const course = getCourseByIdStatic(courseId);
        setCourseInfo(course);
      } catch (error) {
        console.error('Erreur lors du chargement des infos du cours:', error);
      }
    };
    
    loadCourseInfo();
  }, [courseId]);

  // Scroll vers le haut quand on change de chapitre
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [decodedChapitreId]);

  // Fonction pour vérifier l'accès au cours
  const hasAccessToCourse = () => {
    if (!courseInfo) return false;
    return courseInfo.isFree || hasAccessToCourses();
  };
  
  // Rediriger vers la page de connexion si l'utilisateur n'est pas connecté
  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { replace: true });
    }
  }, [currentUser, navigate]);

  // Rediriger vers l'introduction du cours si l'utilisateur n'a pas accès au cours
  useEffect(() => {
    if (currentUser && courseInfo && !hasAccessToCourse()) {
      console.log('Accès au cours refusé - redirection vers introduction du cours');
      navigate(`/cours/${courseId}`, { replace: true });
    }
  }, [currentUser, courseInfo, hasAccessToCourse, navigate, courseId]);
  
  // Récupérer les informations du chapitre actuel depuis la structure du cours
  const currentChapter = courseStructure.chapters.find(chapter => chapter.id === decodedChapitreId) || 
                         courseStructure.chapters.find(chapter => chapter.id === 'chapitre1');

  // Déterminer le contenu à afficher en fonction du chapitreId
  const ContentComponent = chapterContents[decodedChapitreId] || chapterContents['chapitre1'];
  
  // Créer le contenu avec les props nécessaires
  const content = <ContentComponent courseId={courseId} {...contentProps} />;
  
  // Formater le titre du chapitre
  const title = formatTitle(decodedChapitreId || 'chapitre1', currentChapter.title);

  // Fonctions de navigation avec le courseStructure actuel (avec validation)
  const curriedNextChapter = (chapterId) => {
    if (!courseStructure || !courseStructure.chapters) {
      console.warn('courseStructure ou courseStructure.chapters est undefined');
      return null;
    }
    return getNextChapter(courseStructure, chapterId);
  };
  
  const curriedPreviousChapter = (chapterId) => {
    if (!courseStructure || !courseStructure.chapters) {
      console.warn('courseStructure ou courseStructure.chapters est undefined');
      return null;
    }
    return getPreviousChapter(courseStructure, chapterId);
  };

  // Afficher un indicateur de chargement uniquement pendant le chargement initial des achats
  if (!courseInfo) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Props communs à tous les layouts
  const commonLayoutProps = {
          courseStructure: courseStructure,
      courseId: courseId,
      isPurchased: hasAccessToCourse(), // Vérifie l'accès au cours (Pass Codeur ou cours gratuit)
      ...layoutProps
  };

  // Fonction pour extraire le numéro du chapitre depuis l'ID
  const getChapterNumber = (chapterId) => {
    if (!chapterId) return '1';
    
    // Extraire le numéro depuis l'ID (ex: 'chapitre1' -> '1', 'chapitre2' -> '2')
    const match = chapterId.match(/chapitre(\d+)/i);
    if (match) {
      return match[1];
    }
    
    // Si c'est 'introduction', on peut retourner 'Introduction' ou un numéro spécial
    if (chapterId === 'introduction') {
      return 'Introduction';
    }
    
    return '1'; // Valeur par défaut
  };

  return (
    <LayoutComponent {...commonLayoutProps}>
      {/* Breadcrumbs */}
      <Breadcrumbs items={generateCourseBreadcrumbs(courseInfo, title)} />
      
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-2">{title}</h1>
        <div className="text-muted-foreground mb-8">
          {decodedChapitreId === 'introduction' ? 'Introduction' : `Chapitre ${getChapterNumber(decodedChapitreId || 'chapitre1')}`}
        </div>
        
        
        <div className="prose prose-lg max-w-none space-y-6">
          {content}
        </div>

        {/* Navigation entre les chapitres */}
        <div className="mt-12 pt-6">
        <Separator type="line" color="gray-dark" noPaddingTop />
          <CourseNavigation
            courseId={courseId}
            chapterId={chapitreId}
            getNextChapter={curriedNextChapter}
            getPreviousChapter={curriedPreviousChapter}
          />
        </div>
      </div>
    </LayoutComponent>
  );
};

export default GenericCourseContainer; 